import React, { useCallback, useContext, useMemo } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { useField } from 'formik'
import { YStack } from 'tamagui'

import { Text } from '../Text'
import { Icon } from '../Icon'
import { FormFieldContext } from './shared'
import { SearchableCombobox, ComboboxSection } from './SearchableCombobox'

import {
  searchCities,
  getTimezoneDisplayLabel,
  CityEntry,
} from '../../data/cityTimezones'

export const FormTimezonePicker = ({
  placeholder,
}: {
  placeholder?: string
}) => {
  const { t } = useLingui()
  const formFieldContext = useContext(FormFieldContext)
  const [field, , helpers] = useField<string>(formFieldContext.name)

  const displayLabel = useMemo(() => {
    if (!field.value) return null
    return getTimezoneDisplayLabel(field.value)
  }, [field.value])

  // Cities only appear once something is typed.
  const getSections = useCallback(
    (query: string): ComboboxSection<CityEntry>[] =>
      query ? [{ key: 'cities', items: searchCities(query) }] : [],
    []
  )

  const handleSelect = useCallback(
    (entry: CityEntry) => {
      void helpers.setValue(entry.timezone)
      void helpers.setTouched(true)
    },
    [helpers]
  )

  return (
    <SearchableCombobox<CityEntry>
      getSections={getSections}
      getKey={(entry) => `${entry.city}-${entry.timezone}`}
      onSelect={handleSelect}
      isSelected={(entry) => entry.timezone === field.value}
      placeholder={placeholder ?? t`Search for a city...`}
      label={t`Time Zone`}
      valueLabel={
        displayLabel
          ? `${displayLabel.cityLabel}, ${displayLabel.timezoneLabel}`
          : undefined
      }
      onOpen={formFieldContext.onFocus}
      onClose={formFieldContext.onBlur}
      renderEmpty={(query) => (
        <Text size='$4' color='$colorMuted'>
          <Trans>No cities found matching &ldquo;{query}&rdquo;</Trans>
        </Text>
      )}
      renderOption={(entry) => (
        <YStack flex={1} gap='$1'>
          <Text size='$4' color='$colorStrong' fontWeight='$6'>
            {entry.city}, {entry.country}
          </Text>
          <Text size='$3' color='$colorMuted'>
            {getTimezoneDisplayLabel(entry.timezone).timezoneLabel}
          </Text>
        </YStack>
      )}
      renderTrigger={() => (
        <>
          <Icon name='globe-clock' size='$4' color='$colorMuted' />
          {displayLabel ? (
            <YStack flex={1} gap={2}>
              <Text size='$4' color='$colorStrong' fontWeight='$5'>
                {displayLabel.cityLabel}
              </Text>
              <Text size='$3' color='$colorMuted'>
                {displayLabel.timezoneLabel}
              </Text>
            </YStack>
          ) : (
            <Text size='$4' color='$colorMuted' flex={1}>
              <Trans>Select a timezone</Trans>
            </Text>
          )}
        </>
      )}
    />
  )
}
